import { MARCADORES_POR_FOLHA } from "@foto/shared";

/**
 * O progresso da geração dos marcadores, sem tela.
 *
 * O servidor não emite progresso: `gerar` e `refazer` são uma chamada só, que volta pronta ou
 * falha. Tudo o que a faixa mostra sai de três coisas que a tela já sabe — o estado da chamada, há
 * quanto tempo ela começou e o que voltou. Por isso não existem "etapas" aqui: inventar "analisando
 * a foto… 30%" seria mentir com cara de informação. A conta mora neste arquivo, pura e testável, e
 * o componente só desenha o resultado dela.
 */

export const NOME_DO_SISTEMA = "Foto Interativa";

/** Quanto leva criar os três marcadores: três chamadas em paralelo e a montagem da folha. */
export const SEGUNDOS_PARA_A_FOLHA = 40;

/**
 * Quanto leva refazer um: uma chamada ao modelo de imagem só, e as outras duas faixas já existem.
 * É o mesmo "cerca de 30 segundos" que o "Refazer este" sempre disse (e que o servidor documenta);
 * se um dia medirem outra coisa, este é o único lugar a mudar.
 */
export const SEGUNDOS_PARA_UM_MARCADOR = 30;

/** Passado este múltiplo do esperado, a faixa deixa de dizer "leva cerca de" e admite a demora. */
const FATOR_DE_DEMORA = 1.5;

/** Depois deste tempo com a aba à vista, o aviso de "pronto" sai sozinho. */
export const MS_ATE_SUMIR = 8000;

export type Fase = "ocioso" | "gerando" | "refazendo" | "pronto" | "refeito" | "parcial" | "erro";

/** Esperando o servidor: a faixa conta o tempo. */
export const estaEsperando = (fase: Fase): boolean => fase === "gerando" || fase === "refazendo";

/** A chamada acabou, bem ou mal. */
export const terminou = (fase: Fase): boolean =>
  fase === "pronto" || fase === "refeito" || fase === "parcial" || fase === "erro";

/** "0:23", "1:05" — e "1:02:03" se alguém deixar a aba aberta por horas. */
export function formatarTempo(segundos: number): string {
  const total = Math.max(0, Math.floor(segundos));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}

const esperadoDe = (fase: Fase): number =>
  fase === "refazendo" ? SEGUNDOS_PARA_UM_MARCADOR : SEGUNDOS_PARA_A_FOLHA;

/** Já passou do que a espera costuma levar? */
export function estaDemorando(fase: Fase, segundos: number): boolean {
  return estaEsperando(fase) && segundos >= esperadoDe(fase) * FATOR_DE_DEMORA;
}

const PODE_SAIR = "Pode sair desta tela: o resultado fica em “Já feitos”.";

/** O texto de apoio da espera: o que esperar e, passando do esperado, a verdade sobre a demora. */
export function textoDaEspera(fase: Fase, segundos: number): string {
  const demora = estaDemorando(fase, segundos);
  if (fase === "refazendo") {
    return demora
      ? "Está demorando mais que o normal. A faixa nova aparece na folha quando terminar."
      : `A IA pinta só esta faixa; leva cerca de ${SEGUNDOS_PARA_UM_MARCADOR} segundos. As outras duas ficam como estão.`;
  }
  return demora
    ? `Está demorando mais que o normal. A folha aparece quando terminar. ${PODE_SAIR}`
    : `A IA pinta cada faixa; leva cerca de ${SEGUNDOS_PARA_A_FOLHA} segundos. ${PODE_SAIR}`;
}

/**
 * Quanto da faixa aparece pintado quando não há animação (movimento reduzido): o tempo decorrido
 * sobre o esperado, cada faixa num ritmo, e nunca cheia — só o servidor diz que acabou. Em passos de
 * 5% para a faixa não se mexer a cada segundo: quem pediu menos movimento recebe um quadro quase
 * parado, e o cronômetro é o que anda.
 */
const RITMOS = [1, 0.8, 0.6] as const;
const TETO_DO_PREENCHIMENTO = 0.9;

export function nivelPorTempo(segundos: number, esperado: number, indice: number): number {
  const ritmo = RITMOS[indice % RITMOS.length]!;
  const bruto = Math.max(0, (segundos / esperado) * ritmo);
  return Math.round(Math.min(bruto, TETO_DO_PREENCHIMENTO) * 20) / 20;
}

/**
 * Uma faixa em miniatura.
 *
 * - `pintando`: a IA ainda trabalha nela. Com movimento, a tinta desce e some em ciclo, sem prometer
 *   um fim; sem movimento, fica no `nivel` pelo tempo.
 * - `cheia`: entregue. `chegando` = acabou de ficar pronta, e a tinta desce uma vez até o fim.
 * - `vazia`: não existe (a IA não entregou, ou a chamada falhou).
 */
export interface Tinta {
  situacao: "pintando" | "cheia" | "vazia";
  nivel: number;
  chegando: boolean;
}

export type Tom = "calmo" | "ambar" | "erro";

export interface AcaoDaFaixa {
  /** `ver` rola até o resultado; `repetir` refaz o pedido que falhou. */
  tipo: "ver" | "repetir";
  rotulo: string;
}

export interface EstadoDaFaixa {
  tom: Tom;
  titulo: string;
  texto: string;
  /** O cronômetro, só enquanto espera. */
  tempo: string | null;
  tintas: Tinta[];
  acao: AcaoDaFaixa | null;
  /** O que o leitor de tela ouve: muda no começo e no fim, nunca a cada segundo. */
  anuncio: string;
}

export interface EntradaDaFaixa {
  fase: Fase;
  /** Segundos desde que a chamada começou (ou até ela terminar). */
  segundos: number;
  /** `pronto`/`parcial`: quantos marcadores a IA entregou. */
  entregues?: number;
  /** `parcial`: os índices (0, 1, 2) que faltaram. */
  faltam?: number[];
  /** `refazendo`/`refeito`/`erro` de um marcador: qual é (0, 1 ou 2). */
  indice?: number;
  /** `erro`: o que o servidor disse. */
  mensagem?: string;
}

const todas = (f: (i: number) => Tinta): Tinta[] =>
  Array.from({ length: MARCADORES_POR_FOLHA }, (_, i) => f(i));

const pintando =
  (segundos: number, esperado: number) =>
  (i: number): Tinta => ({
    situacao: "pintando",
    nivel: nivelPorTempo(segundos, esperado, i),
    chegando: false,
  });
const cheia = (chegando: boolean): Tinta => ({ situacao: "cheia", nivel: 1, chegando });
const vazia = (): Tinta => ({ situacao: "vazia", nivel: 0, chegando: false });

const fotos = (n: number) => `${n} ${n === 1 ? "foto" : "fotos"}`;

/** "o marcador 2", "os marcadores 1 e 3" — com o verbo no número certo. */
export function quemFaltou(faltam: number[]): string {
  const n = faltam.map((i) => i + 1);
  if (n.length === 1) return `Faltou o marcador ${n[0]}`;
  return `Faltaram os marcadores ${n.slice(0, -1).join(", ")} e ${n[n.length - 1]}`;
}

/**
 * Tudo o que a faixa mostra, a partir do que a tela sabe. Nulo = nada a mostrar (ocioso).
 *
 * Os textos seguem o mesmo vocabulário do resto da tela ("marcador", "faixa", "folha", "Já feitos"):
 * a faixa não ensina nome novo, só diz onde a conta está.
 */
export function estadoDaFaixa(e: EntradaDaFaixa): EstadoDaFaixa | null {
  const tempo = formatarTempo(e.segundos);
  const numero = (e.indice ?? 0) + 1;

  switch (e.fase) {
    case "ocioso":
      return null;

    case "gerando": {
      const titulo = "Criando os marcadores";
      return {
        tom: "calmo",
        titulo,
        texto: textoDaEspera(e.fase, e.segundos),
        tempo,
        tintas: todas(pintando(e.segundos, SEGUNDOS_PARA_A_FOLHA)),
        acao: null,
        anuncio: `${titulo}. Leva cerca de ${SEGUNDOS_PARA_A_FOLHA} segundos.`,
      };
    }

    case "refazendo": {
      const titulo = `Refazendo o marcador ${numero}…`;
      return {
        tom: "calmo",
        titulo,
        texto: textoDaEspera(e.fase, e.segundos),
        tempo,
        // Só a faixa refeita pinta: as outras duas já existem e continuam inteiras.
        tintas: todas((i) =>
          i === e.indice ? pintando(e.segundos, SEGUNDOS_PARA_UM_MARCADOR)(i) : cheia(false),
        ),
        acao: null,
        anuncio: `Refazendo o marcador ${numero}. Leva cerca de ${SEGUNDOS_PARA_UM_MARCADOR} segundos.`,
      };
    }

    case "pronto": {
      const n = e.entregues ?? MARCADORES_POR_FOLHA;
      const titulo = n === 1 ? "Pronto: 1 marcador" : `Prontos: ${n} marcadores`;
      return {
        tom: "calmo",
        titulo,
        texto: `Levou ${tempo} e consumiu ${fotos(n)} do pacote.`,
        tempo: null,
        tintas: todas(() => cheia(true)),
        acao: { tipo: "ver", rotulo: "Ver o conjunto" },
        anuncio: `${titulo}.`,
      };
    }

    case "refeito": {
      const titulo = `Marcador ${numero} refeito`;
      return {
        tom: "calmo",
        titulo,
        texto: `Levou ${tempo} e consumiu 1 foto do pacote.`,
        tempo: null,
        tintas: todas((i) => cheia(i === e.indice)),
        acao: { tipo: "ver", rotulo: "Ver o conjunto" },
        anuncio: `${titulo}.`,
      };
    }

    case "parcial": {
      const entregues = e.entregues ?? 0;
      const faltam =
        e.faltam && e.faltam.length > 0
          ? e.faltam
          : Array.from({ length: MARCADORES_POR_FOLHA - entregues }, (_, k) => entregues + k);
      const titulo = `A IA entregou ${entregues} de ${MARCADORES_POR_FOLHA}`;
      return {
        tom: "ambar",
        titulo,
        texto: `${quemFaltou(faltam)}. Refazer custa 1 foto${faltam.length > 1 ? " por marcador" : ""}.`,
        tempo: null,
        tintas: todas((i) => (faltam.includes(i) ? vazia() : cheia(true))),
        acao: { tipo: "ver", rotulo: "Ver o que faltou" },
        anuncio: `${titulo}. ${quemFaltou(faltam)}.`,
      };
    }

    case "erro": {
      const um = e.indice !== undefined;
      const titulo = um ? `O marcador ${numero} não saiu` : "Os marcadores não saíram";
      const mensagem = e.mensagem?.trim() || "Não foi possível falar com o servidor.";
      return {
        tom: "erro",
        titulo,
        texto: mensagem,
        tempo: null,
        // Refazendo um, as outras duas continuam valendo; criando a folha, nada ficou pronto.
        tintas: todas((i) => (um && i !== e.indice ? cheia(false) : vazia())),
        acao: um
          ? { tipo: "ver", rotulo: "Ver o conjunto" }
          : { tipo: "repetir", rotulo: "Tentar de novo" },
        anuncio: `${titulo}. ${mensagem}`,
      };
    }
  }
}

/**
 * Depois de quanto tempo a faixa sai sozinha; nulo = fica até a pessoa fechar. Só o que deu certo
 * sai: o resultado já está na página, e o aviso era só para quem não estava olhando. O parcial e o
 * erro pedem uma decisão, e um aviso que some antes de ser lido é pior do que nenhum.
 */
export function msAteSumir(fase: Fase): number | null {
  return fase === "pronto" || fase === "refeito" ? MS_ATE_SUMIR : null;
}

/**
 * O título da aba. Dura a geração (40 s dão tempo de trocar de aba) e, se ela termina com a aba
 * escondida, avisa até a pessoa voltar. Nulo = o título de sempre.
 */
export function tituloDaAba(
  fase: Fase,
  fimComAbaEscondida: boolean,
  indice?: number,
): string | null {
  const sufixo = ` · ${NOME_DO_SISTEMA}`;
  if (fase === "gerando") return `Criando marcadores…${sufixo}`;
  if (fase === "refazendo") return `Refazendo o marcador ${(indice ?? 0) + 1}…${sufixo}`;
  if (!fimComAbaEscondida) return null;
  if (fase === "pronto") return `Marcadores prontos${sufixo}`;
  if (fase === "refeito") return `Marcador refeito${sufixo}`;
  if (fase === "parcial") return `Faltou marcador${sufixo}`;
  if (fase === "erro") return `Marcadores não saíram${sufixo}`;
  return null;
}

/** O estado de uma chamada ao servidor, como a tela a observa. */
export interface PedidoObservado {
  estado: "idle" | "pending" | "success" | "error";
  /** Quando a chamada começou (ms desde 1970). */
  enviadoEm: number;
  /** `refazer`: qual marcador. */
  indice?: number;
  /** `gerar`: quantos a IA entregou e quais faltaram. */
  entregues?: number;
  faltam?: number[];
  /** `error`: o que o servidor disse. */
  mensagem?: string;
}

export interface Evento {
  fase: Fase;
  /** Identifica esta ocorrência: fechar a faixa vale para ela, e a próxima volta a aparecer. */
  chave: string;
  /** Quando começou (ms desde 1970); nulo = ocioso. */
  desde: number | null;
  entregues?: number;
  faltam?: number[];
  indice?: number;
  mensagem?: string;
}

export const OCIOSO: Evento = { fase: "ocioso", chave: "ocioso", desde: null };

/**
 * Qual das duas chamadas — criar a folha ou refazer uma faixa — a faixa deve mostrar: a mais recente.
 * `montadoEm` descarta o que ficou no cache de uma visita anterior à tela (uma refeita de ontem não
 * pode abrir a faixa de hoje).
 */
export function eventoDoProgresso(
  gerar: PedidoObservado | null,
  refazer: PedidoObservado | null,
  montadoEm: number,
): Evento {
  const candidatos = [
    gerar && { origem: "gerar" as const, p: gerar },
    refazer && { origem: "refazer" as const, p: refazer },
  ].filter(
    (c): c is NonNullable<typeof c> =>
      c !== null && c.p.estado !== "idle" && c.p.enviadoEm >= montadoEm,
  );
  if (candidatos.length === 0) return OCIOSO;

  const { origem, p } = candidatos.reduce((a, b) => (b.p.enviadoEm > a.p.enviadoEm ? b : a));
  const chave = `${origem}-${p.enviadoEm}-${p.estado}`;
  const base = { chave, desde: p.enviadoEm };

  if (origem === "refazer") {
    const fase: Fase =
      p.estado === "pending" ? "refazendo" : p.estado === "success" ? "refeito" : "erro";
    return { ...base, fase, indice: p.indice, ...(p.mensagem && { mensagem: p.mensagem }) };
  }

  if (p.estado === "pending") return { ...base, fase: "gerando" };
  if (p.estado === "error") return { ...base, fase: "erro", mensagem: p.mensagem };
  const entregues = p.entregues ?? MARCADORES_POR_FOLHA;
  return {
    ...base,
    fase: entregues < MARCADORES_POR_FOLHA ? "parcial" : "pronto",
    entregues,
    ...(p.faltam && { faltam: p.faltam }),
  };
}
